/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * New campaign rateplan card header
 *
 * @author Nikolay Sviridov
 * @version 1.39.0
 * @since 1.39.0
 */

import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { icons } from '@/main/assets';
import { colors } from '@/main/theme';
import { ImgStyled } from '@components/common/Block/Img';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { stringFormatParams } from '@utils/stringFormat';

import {
	RatePlanCard2025HeadStyled,
	RatePlanCard2025Title,
	RatePlanIconStyled,
	TitleCategory,
	TitleRateplan,
	TitleRateplanAndIcon,
} from './styled/RatePlanCard2025HeadStyled';


interface RatePlanCard2025TitleProps {
	categoryName?: string;
	rateplanName: string;
	backgroundColor?: string;
	rateplanIcon?: string;
    connected?: number;
    limitConnections?: number;
}

export const RatePlanCard2025Head = (props: RatePlanCard2025TitleProps) => {
	const { backgroundColor, categoryName, rateplanName, rateplanIcon, limitConnections, connected } = props;

    const productTranslation = useSelector(makeSelectTranslationPageProducts());

    const limitConnectionsTitle = stringFormatParams(productTranslation.limitConnections, {
        limitConnections: limitConnections,
        connected: connected,
    });

	const textColor = useMemo(
		() => (backgroundColor === colors.Green ? colors.ClearWhite : colors.FreshAsphalt),
		[backgroundColor],
	);

	return (
		<RatePlanCard2025HeadStyled $backgroundColor={backgroundColor}>
			<RatePlanCard2025Title>
				{categoryName && (
					<TitleCategory
                        $textColor={textColor}
						titleVariant='body2'
					>
						{categoryName}
					</TitleCategory>
				)}
				<TitleRateplanAndIcon>
					<TitleRateplan
						$textColor={textColor}
                        lines={3}
						titleVariant='h4'
					>
						{rateplanName}
					</TitleRateplan>
					{rateplanIcon ? (
						<RatePlanIconStyled $iconColor={textColor}>
							<img
								alt='rateplanIcon'
								height='20px'
								src={rateplanIcon}
								width='20px'
							/>
						</RatePlanIconStyled>
					) : (
						''
					)}
				</TitleRateplanAndIcon>
			</RatePlanCard2025Title>
            {!!limitConnections && (<ArrowTooltip limit-connections-toolip='limitConnectionsTooltip' title={limitConnectionsTitle}>
                <ImgStyled
                    className="small"
                    margin={'0px 8px 0px 0px'}
                    src={icons.infoPurpleIcon}
                    width={'20px'}
                />
            </ArrowTooltip>)}
		</RatePlanCard2025HeadStyled>
	);
};
